import { useState, useCallback, useEffect, useMemo } from "react";
import { Loader2 } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import EpisodeRow from "./EpisodeRow";
import { checkTranscribed, transcribeEpisode } from "@/services/podcastApi";
import { useTranscriptionJobs } from "@/contexts/TranscriptionContext";
import { useAuth } from "@/contexts/AuthContext";
import { apiFetch, InsufficientBalanceError } from "@/lib/api";
import InsufficientBalanceModal from "@/components/InsufficientBalanceModal";
import type { Episode } from "@/types/podcast";

import { ADMIN_EMAILS } from "../../lib/adminEmails";

interface BalanceResponse {
  minutes_remaining: number;
  is_free_tier: boolean;
}

interface EpisodeListProps {
  episodes: Episode[];
  podcastName: string;
  podcastArtworkUrl?: string;
  feedUrl?: string;
  totalEpisodes: number;
  isLoading?: boolean;
  onLoadMore?: () => void;
  isLoadingMore?: boolean;
}

export default function EpisodeList({
  episodes,
  podcastName,
  podcastArtworkUrl,
  feedUrl,
  totalEpisodes,
  isLoading,
  onLoadMore,
  isLoadingMore,
}: EpisodeListProps) {
  const { addJob, addBatchPoll } = useTranscriptionJobs();
  const { user } = useAuth();
  const isAdmin = !!user && ADMIN_EMAILS.includes(user.email);
  
  const [selectedGuids, setSelectedGuids] = useState<Set<string>>(new Set());
  const [transcribedMap, setTranscribedMap] = useState<Record<string, number>>({});
  const [transcribingGuids, setTranscribingGuids] = useState<Set<string>>(new Set());
  const [transcribedFilter, setTranscribedFilter] = useState<"all" | "transcribed" | "not-transcribed">("all");
  const [batchProgress, setBatchProgress] = useState<string | null>(null);
  const [balance, setBalance] = useState<number | null>(null);
  const [showBalanceModal, setShowBalanceModal] = useState(false);
  const [modalMinutesNeeded, setModalMinutesNeeded] = useState(0);
  const [pendingEpisode, setPendingEpisode] = useState<Episode | null>(null);
  
  // Guest upsell modal state (shown when content exceeds remaining minutes)
  const [guestUpsell, setGuestUpsell] = useState<{
    show: boolean;
    episode: Episode;
    contentMinutes: number;
    remainingMinutes: number;
  } | null>(null);

  // Filter episodes by transcribed status (must be before any early returns!)
  const filteredEpisodes = useMemo(() => {
    if (transcribedFilter === "all") return episodes;
    if (transcribedFilter === "transcribed") {
      return episodes.filter((e) => e.audio_url in transcribedMap);
    }
    return episodes.filter((e) => !(e.audio_url in transcribedMap));
  }, [episodes, transcribedMap, transcribedFilter]);

  const allSelected = selectedGuids.size === filteredEpisodes.length && filteredEpisodes.length > 0;

  // Fetch balance on mount (skip for admin)
  useEffect(() => {
    if (isAdmin) {
      setBalance(999999); // Admin has unlimited
      return;
    }
    apiFetch<BalanceResponse>("/billing/balance")
      .then((data) => setBalance(data.minutes_remaining))
      .catch(() => setBalance(null)); // Guest or error - will rely on backend 402
  }, [isAdmin]);

  // Calculate total duration of selected episodes (in minutes)
  const selectedDurationMinutes = useMemo(() => {
    const selected = episodes.filter((e) => selectedGuids.has(e.guid));
    const totalSeconds = selected.reduce((sum, e) => sum + (e.duration || 0), 0);
    return Math.ceil(totalSeconds / 60);
  }, [episodes, selectedGuids]);

  // Cached durations for episodes (from global cache, fallback when RSS doesn't have duration)
  const [cachedDurations, setCachedDurations] = useState<Record<string, number>>({});

  // Check which episodes are already transcribed when episodes change
  useEffect(() => {
    if (episodes.length === 0) return;
    const urls = episodes.map((e) => e.audio_url).filter(Boolean);
    if (urls.length === 0) return;

    checkTranscribed(urls).then((map) => {
      setTranscribedMap((prev) => ({ ...prev, ...map }));
    });
  }, [episodes]);

  // Fetch cached durations for episodes without RSS duration
  useEffect(() => {
    const urlsWithoutDuration = episodes
      .filter((e) => !e.duration || e.duration === 0)
      .map((e) => e.audio_url)
      .filter(Boolean);
    
    if (urlsWithoutDuration.length === 0) return;

    apiFetch<{ cached: Record<string, { duration_sec: number }> }>("/podcasts/check-cached", {
      method: "POST",
      body: JSON.stringify({ audio_urls: urlsWithoutDuration }),
    }).then((data) => {
      const durations: Record<string, number> = {};
      for (const [url, info] of Object.entries(data.cached)) {
        durations[url] = info.duration_sec;
      }
      setCachedDurations((prev) => ({ ...prev, ...durations }));
    }).catch(() => {
      // Ignore errors - cached duration is optional
    });
  }, [episodes]);

  const toggleSelect = useCallback((guid: string) => {
    setSelectedGuids((prev) => {
      const next = new Set(prev);
      if (next.has(guid)) next.delete(guid);
      else next.add(guid);
      return next;
    });
  }, []);

  const toggleSelectAll = useCallback(() => {
    setSelectedGuids((prev) => {
      // Use filteredEpisodes for select all
      const currentFiltered = transcribedFilter === "all" 
        ? episodes 
        : transcribedFilter === "transcribed"
          ? episodes.filter((e) => e.audio_url in transcribedMap)
          : episodes.filter((e) => !(e.audio_url in transcribedMap));
      
      if (prev.size === currentFiltered.length && currentFiltered.length > 0) return new Set();
      return new Set(currentFiltered.map((e) => e.guid));
    });
  }, [episodes, transcribedFilter, transcribedMap]);

  const proceedWithTranscription = useCallback(
    async (episode: Episode) => {
      const episodeMinutes = episode.duration ? Math.ceil(episode.duration / 60) : 0;
      
      setTranscribingGuids((prev) => new Set(prev).add(episode.guid));
      try {
        const response = await transcribeEpisode(episode.audio_url, {
          title: episode.title,
          podcast_name: podcastName,
          description: episode.description,
          artwork_url: podcastArtworkUrl || "",
          feed_url: feedUrl || "",
        }, "auto");
        
        // Track job in global context for bottom bar
        addJob(response.job_id, episode.title);
        // Deduct from local balance estimate
        if (balance !== null && episodeMinutes > 0) {
          setBalance((prev) => (prev !== null ? Math.max(0, prev - episodeMinutes) : null));
        }
        // Stay on current page — bottom bar shows progress
      } catch (err) {
        if (err instanceof InsufficientBalanceError) {
          setModalMinutesNeeded(err.minutes_needed);
          setShowBalanceModal(true);
        } else {
          console.error("Transcription failed:", err);
        }
        setTranscribingGuids((prev) => {
          const next = new Set(prev);
          next.delete(episode.guid);
          return next;
        });
      }
    },
    [podcastName, podcastArtworkUrl, addJob, balance, feedUrl],
  );

  const handleTranscribe = useCallback(
    async (episode: Episode) => {
      // Use RSS duration, or fall back to cached duration if available
      const rssDuration = episode.duration || 0;
      const cachedDuration = cachedDurations[episode.audio_url] || 0;
      const effectiveDuration = rssDuration || cachedDuration;
      const episodeMinutes = effectiveDuration ? Math.ceil(effectiveDuration / 60) : 0;
      const guestRemaining = balance ?? 45;
      
      // For guests with unknown duration (common in RSS feeds), assume it might exceed limit
      // Show warning if duration unknown OR if known duration exceeds remaining
      if (!user) {
        const durationUnknown = !effectiveDuration;
        const exceedsLimit = episodeMinutes > guestRemaining;
        
        if (durationUnknown || exceedsLimit) {
          setGuestUpsell({
            show: true,
            episode,
            contentMinutes: durationUnknown ? 0 : episodeMinutes, // 0 means unknown
            remainingMinutes: guestRemaining,
          });
          return;
        }
        // Content fits within limit - proceed without modal
        proceedWithTranscription(episode);
        return;
      }
      
      // Check balance before starting (if we have duration info) - skip for admin
      if (!isAdmin && balance !== null && episodeMinutes > 0 && episodeMinutes > balance) {
        setModalMinutesNeeded(episodeMinutes - balance);
        setPendingEpisode(episode);
        setShowBalanceModal(true);
        return;
      }

      proceedWithTranscription(episode);
    },
    [user, isAdmin, balance, proceedWithTranscription, cachedDurations],
  );

  // Guest batch upsell modal state
  const [guestBatchUpsell, setGuestBatchUpsell] = useState<{
    show: boolean;
    totalMinutes: number;
    remainingMinutes: number;
    episodes: Episode[];
  } | null>(null);

  const proceedWithBatchTranscription = useCallback(async (selected: Episode[]) => {
    if (selected.length === 0) return;

    setBatchProgress(`Submitting ${selected.length} episode${selected.length !== 1 ? 's' : ''}...`);
    selected.forEach((e) => setTranscribingGuids((prev) => new Set(prev).add(e.guid)));

    // Build episode_metadata map: audio_url → metadata
    const episodeMetadata: Record<string, {
      title: string;
      podcast_name: string;
      feed_url: string;
      description: string;
      published_date?: string;
      artwork_url?: string;
      duration_ms?: number;
    }> = {};
    for (const ep of selected) {
      episodeMetadata[ep.audio_url] = {
        title: ep.title || "",
        podcast_name: podcastName,
        feed_url: feedUrl || "",
        description: ep.description || "",
        published_date: ep.published_date || undefined,
        artwork_url: podcastArtworkUrl || undefined,
        duration_ms: ep.duration ? ep.duration * 1000 : undefined,
      };
    }

    try {
      const response = await apiFetch<{ batch_id: string; items: Array<{ id: string; url: string }> }>(
        "/batch",
        {
          method: "POST",
          body: JSON.stringify({
            urls: selected.map((e) => e.audio_url),
            episode_metadata: episodeMetadata,
          }),
        }
      );

      // Hand off batch tracking to the context's batch poller (GET /batch/{id})
      // — survives navigation; do NOT use addJob (single-job /job/{id} poller),
      // batch_item ids return not_found there. Bug fixed in this commit.
      const itemByUrl = new Map(response.items.map(item => [item.url, item.id]));
      const trackedItems: Array<{ id: string; title?: string }> = [];
      for (const ep of selected) {
        const itemId = itemByUrl.get(ep.audio_url);
        if (itemId) trackedItems.push({ id: itemId, title: ep.title });
      }
      if (trackedItems.length > 0) {
        addBatchPoll(response.batch_id, trackedItems);
      }
      // Bottom-bar now drives progress UI; clear per-row spinners to avoid
      // a permanent Loader2 (the row-level transcribingGuids has no terminal
      // signal of its own once handed off). selectedGuids is cleared in finally.
      setTranscribingGuids((prev) => {
        const next = new Set(prev);
        selected.forEach((e) => next.delete(e.guid));
        return next;
      });

      const minutesDeducted = selected.reduce(
        (sum, e) => sum + (e.duration ? Math.ceil(e.duration / 60) : 0),
        0
      );
      if (balance !== null && minutesDeducted > 0) {
        setBalance((prev) => (prev !== null ? Math.max(0, prev - minutesDeducted) : null));
      }
    } catch (err) {
      // Revert transcribing state on failure
      selected.forEach((e) =>
        setTranscribingGuids((prev) => {
          const next = new Set(prev);
          next.delete(e.guid);
          return next;
        })
      );
      if (err instanceof InsufficientBalanceError) {
        setModalMinutesNeeded(err.minutes_needed);
        setShowBalanceModal(true);
      } else {
        console.error("Batch transcription failed:", err);
      }
    } finally {
      setBatchProgress(null);
      setSelectedGuids(new Set());
    }
  }, [podcastName, feedUrl, addJob, addBatchPoll, balance]);

  const handleBatchTranscribe = useCallback(async () => {
    const selected = episodes.filter((e) => selectedGuids.has(e.guid));
    if (selected.length === 0) return;

    const guestRemaining = balance ?? 45;

    // For guests, show warning if batch exceeds remaining minutes
    if (!user && selectedDurationMinutes > guestRemaining) {
      setGuestBatchUpsell({
        show: true,
        totalMinutes: selectedDurationMinutes,
        remainingMinutes: guestRemaining,
        episodes: selected,
      });
      return;
    }

    // Check balance before starting batch - skip for admin and guests
    if (user && !isAdmin && balance !== null && selectedDurationMinutes > 0 && selectedDurationMinutes > balance) {
      setModalMinutesNeeded(selectedDurationMinutes - balance);
      setShowBalanceModal(true);
      return;
    }

    proceedWithBatchTranscription(selected);
  }, [episodes, selectedGuids, balance, selectedDurationMinutes, isAdmin, user, proceedWithBatchTranscription]);

  // Proceed with transcription anyway (guest 45min limit applies)
  const handleProceedAnyway = useCallback(async () => {
    if (!pendingEpisode) return;
    
    const episode = pendingEpisode;
    setPendingEpisode(null);
    
    setTranscribingGuids((prev) => new Set(prev).add(episode.guid));
    try {
      const response = await transcribeEpisode(episode.audio_url, {
        title: episode.title,
        podcast_name: podcastName,
        description: episode.description,
        artwork_url: podcastArtworkUrl || "",
        feed_url: feedUrl || "",
      }, "auto");
      addJob(response.job_id, episode.title);
    } catch (err) {
      console.error("Transcription failed:", err);
      setTranscribingGuids((prev) => {
        const next = new Set(prev);
        next.delete(episode.guid);
        return next;
      });
    }
  }, [pendingEpisode, podcastName, podcastArtworkUrl, feedUrl, addJob]);

  if (isLoading) {
    return (
      <div className="space-y-3">
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="flex items-start gap-3 rounded-lg border border-gray-100 bg-white px-4 py-3">
            <Skeleton className="mt-1 h-4 w-4 rounded-sm" />
            <div className="flex-1 space-y-2">
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-3 w-full" />
              <Skeleton className="h-6 w-20" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (episodes.length === 0) {
    return (
      <p className="py-12 text-center text-sm text-gray-400">
        This podcast doesn't have any episodes with available audio.
      </p>
    );
  }

  return (
    <div>
      {/* Batch controls - sticky below navbar */}
      <div 
        className="sticky z-30 -mx-4 flex items-center gap-2 border-b border-gray-200 bg-white px-4 py-2 shadow-sm mb-3"
        style={{ top: 'var(--navbar-height, 68px)' }}
      >
        {/* Transcribed filter toggle */}
        <select
          value={transcribedFilter}
          onChange={(e) => {
            setTranscribedFilter(e.target.value as "all" | "transcribed" | "not-transcribed");
            setSelectedGuids(new Set()); // Clear selection when filter changes
          }}
          className="rounded border border-gray-300 bg-white px-2 py-1 text-xs text-gray-700 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 shrink-0"
        >
          <option value="all">All ({episodes.length})</option>
          <option value="transcribed">Transcribed ({episodes.filter((e) => e.audio_url in transcribedMap).length})</option>
          <option value="not-transcribed">Not Transcribed ({episodes.filter((e) => !(e.audio_url in transcribedMap)).length})</option>
        </select>

        <label className="flex items-center gap-1.5 text-xs text-gray-600 shrink-0">
          <Checkbox
            checked={allSelected}
            onCheckedChange={toggleSelectAll}
            className="h-3.5 w-3.5"
          />
          <span className="hidden sm:inline">Select</span> ({filteredEpisodes.length})
        </label>

        <button
          onClick={handleBatchTranscribe}
          disabled={!!batchProgress || selectedGuids.size === 0}
          className={`rounded px-2 py-1 text-xs font-medium transition-colors shrink-0 ${
            selectedGuids.size > 0
              ? 'bg-blue-600 text-white hover:bg-blue-700'
              : 'border border-gray-300 bg-white text-gray-400 cursor-not-allowed'
          } disabled:opacity-50`}
        >
          {batchProgress ?? (selectedGuids.size > 0 ? `Transcribe (${selectedGuids.size})` : 'Transcribe')}
        </button>
        
        {selectedGuids.size > 0 && selectedDurationMinutes > 0 && !isAdmin && (
          <span className={`text-xs truncate ${balance !== null && selectedDurationMinutes > balance ? 'text-red-600' : 'text-gray-500'}`}>
            {selectedDurationMinutes}m selected
            {balance !== null && selectedDurationMinutes > balance && (
              <button
                onClick={() => {
                  setModalMinutesNeeded(selectedDurationMinutes - balance);
                  setShowBalanceModal(true);
                }}
                className="ml-1 underline"
              >
                (+{selectedDurationMinutes - balance} top-up)
              </button>
            )}
          </span>
        )}

        {/* Feedback button - aligned right */}
        <a
          href="https://docs.google.com/forms/d/e/1FAIpQLScqrCEG1vRsZOVuwkwf6RdN1twJQTXIghF7hLbT5PYrPwv_uA/viewform?usp=publish-editor"
          target="_blank"
          rel="noopener noreferrer"
          className="ml-auto inline-flex items-center gap-1 rounded bg-blue-600 px-2 py-1 text-xs font-medium text-white hover:bg-blue-700 shrink-0"
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="h-3.5 w-3.5">
            <path fillRule="evenodd" d="M3.43 2.524A41.29 41.29 0 0110 2c2.236 0 4.43.18 6.57.524 1.437.231 2.43 1.49 2.43 2.902v5.148c0 1.413-.993 2.67-2.43 2.902a41.202 41.202 0 01-5.183.501l-2.9 2.749A.75.75 0 017.25 16.5v-2.33a41.648 41.648 0 01-3.82-.524C2.007 13.34 1 12.076 1 10.674V5.426c0-1.413.993-2.67 2.43-2.902z" clipRule="evenodd" />
          </svg>
          Feedback
        </a>
      </div>

      {/* Episode rows */}
      <div className="space-y-2">
        {filteredEpisodes.map((episode) => (
          <EpisodeRow
            key={episode.guid}
            episode={episode}
            isSelected={selectedGuids.has(episode.guid)}
            onToggleSelect={toggleSelect}
            onTranscribe={handleTranscribe}
            isTranscribed={episode.audio_url in transcribedMap}
            transcriptId={transcribedMap[episode.audio_url]}
            isTranscribing={transcribingGuids.has(episode.guid)}
          />
        ))}
      </div>

      {/* Load more */}
      {onLoadMore && episodes.length < totalEpisodes && transcribedFilter === "all" && (
        <div className="mt-4 text-center">
          <button
            onClick={onLoadMore}
            disabled={isLoadingMore}
            className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            {isLoadingMore ? (
              <span className="inline-flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading...
              </span>
            ) : (
              `Load More (showing ${episodes.length.toLocaleString()} of ${totalEpisodes.toLocaleString()})`
            )}
          </button>
        </div>
      )}

      {/* Guest batch upsell modal - shows when batch total exceeds remaining minutes */}
      {guestBatchUpsell?.show && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl">
            <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-amber-100">
              <svg className="h-6 w-6 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-900">Batch exceeds free limit</h3>
            <p className="mt-2 text-sm text-gray-600">
              Selected episodes total <span className="font-medium">{guestBatchUpsell.totalMinutes} minutes</span>. 
              You have <span className="font-medium">{guestBatchUpsell.remainingMinutes} minutes</span> remaining — the last episode(s) will be truncated.
            </p>
            <div className="mt-4 rounded-lg bg-blue-50 border border-blue-200 p-3">
              <p className="text-sm text-blue-800">
                <span className="font-medium">Sign up free</span> to get 20 hours and transcribe all episodes in full.
              </p>
            </div>
            <div className="mt-6 flex flex-col gap-3">
              <a
                href="/signup"
                className="w-full rounded-lg bg-blue-600 px-4 py-2.5 text-center text-sm font-medium text-white hover:bg-blue-700"
              >
                Sign up for full batch
              </a>
              <button
                onClick={() => {
                  const eps = guestBatchUpsell.episodes;
                  setGuestBatchUpsell(null);
                  proceedWithBatchTranscription(eps);
                }}
                className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Continue (last episodes may be truncated)
              </button>
              <button
                onClick={() => setGuestBatchUpsell(null)}
                className="text-sm text-gray-500 hover:text-gray-700"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Guest upsell modal - shows when content exceeds remaining minutes */}
      {guestUpsell?.show && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl">
            <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-amber-100">
              <svg className="h-6 w-6 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-900">
              {guestUpsell.contentMinutes === 0 ? 'Episode duration unknown' : 'Episode exceeds free limit'}
            </h3>
            <p className="mt-2 text-sm text-gray-600">
              {guestUpsell.contentMinutes === 0 ? (
                <>
                  This episode's duration is unknown. You have <span className="font-medium">{guestUpsell.remainingMinutes} minutes</span> remaining — content beyond that will be truncated.
                </>
              ) : (
                <>
                  This episode is <span className="font-medium">{guestUpsell.contentMinutes} minutes</span>. 
                  You have <span className="font-medium">{guestUpsell.remainingMinutes} minutes</span> remaining — the rest will be truncated.
                </>
              )}
            </p>
            <div className="mt-4 rounded-lg bg-blue-50 border border-blue-200 p-3">
              <p className="text-sm text-blue-800">
                <span className="font-medium">Sign up free</span> to get 20 hours of transcription and transcribe the full episode.
              </p>
            </div>
            <div className="mt-6 flex flex-col gap-3">
              <a
                href="/signup"
                className="w-full rounded-lg bg-blue-600 px-4 py-2.5 text-center text-sm font-medium text-white hover:bg-blue-700"
              >
                Sign up for full episode
              </a>
              <button
                onClick={() => {
                  const episode = guestUpsell.episode;
                  setGuestUpsell(null);
                  proceedWithTranscription(episode);
                }}
                className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Continue (max {guestUpsell.remainingMinutes} min)
              </button>
              <button
                onClick={() => setGuestUpsell(null)}
                className="text-sm text-gray-500 hover:text-gray-700"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Insufficient balance modal */}
      {showBalanceModal && (
        <InsufficientBalanceModal
          minutesNeeded={modalMinutesNeeded}
          minutesRemaining={balance ?? 0}
          onClose={() => {
            setShowBalanceModal(false);
            setPendingEpisode(null);
          }}
          onProceedAnyway={handleProceedAnyway}
          isGuest={!user}
        />
      )}
    </div>
  );
}
